import React, { useContext, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import useClickAway from 'react-use/lib/useClickAway';
import { AppContext } from 'src/components/app-context';
import type { ProductCollectionNonNameSearchFilter } from '../filter/types';
import type { AggregationSection } from '../../../../types';
import FilterSection, { isValidSection } from '../filter-section';
import { FilterFloater } from 'src/components/filter-floater';
import { AppliedFilters } from 'src/components/applied-filters';
import { trackProductCollectionQuickFilterOpen } from '../../tracking';
import { toggleFilter } from '../../../sidebar/actions/filter';

import * as S from './styles';
import {
  OutlinedButton,
  ButtonText,
  ButtonIcon,
  ButtonSize,
} from '@prisjakt/ui/button/variants';
import { IconFilter } from '@prisjakt/ui/icon/filter';
import { formatFilterFn } from '../../utils/filter';

type Props = {
  filters: ProductCollectionNonNameSearchFilter[];
  sections: AggregationSection[];
  clearFilter: (property?: string) => void;
};

export const FeaturedFilters = ({ filters, sections, clearFilter }: Props) => {
  const { t } = useContext(AppContext);
  const [currentOpenFilter, setOpenFilter] = useState<string | null>(null);

  const featuredFiltersRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useDispatch();

  const onFilterToggle = useCallback(
    (id: string) => {
      const newCurrentFilter = currentOpenFilter === id ? null : id;
      setOpenFilter(newCurrentFilter);
      if (newCurrentFilter) {
        trackProductCollectionQuickFilterOpen(
          (sections.find(item => item.id === id) || { property: id }).property
        );
      }
    },
    [currentOpenFilter, sections]
  );

  useClickAway(featuredFiltersRef, () => setOpenFilter(null));

  return (
    <S.FeaturedFiltersWrapper>
      <S.FeaturedFilters ref={featuredFiltersRef}>
        {sections.map(section =>
          isValidSection(section) ? (
            <FilterFloater
              dataTestType={section.__typename}
              dataTestProperty={section.property}
              key={section.id}
              id={section.id}
              isOpen={section.id === currentOpenFilter}
              onFilterToggle={onFilterToggle}
              title={section.title}
            >
              <FilterSection
                section={section}
                useInfiniteScroll
                appliedFilters={filters}
                autoFocusSearch
              />
            </FilterFloater>
          ) : null
        )}
        <OutlinedButton
          size={ButtonSize.SMALL}
          onClick={() => dispatch(toggleFilter())}
          containerProps={{ 'data-test': 'FeaturedFilterMoreFiltersButton' }}
        >
          <ButtonIcon>
            <IconFilter />
          </ButtonIcon>
          <ButtonText>{t('filter', 'filter.show-more-filters')}</ButtonText>
        </OutlinedButton>
      </S.FeaturedFilters>
      <AppliedFilters
        filters={filters}
        filterFormatFn={formatFilterFn}
        onClearFilter={({ property }) => clearFilter(property)}
        onClearAll={() => clearFilter()}
      />
    </S.FeaturedFiltersWrapper>
  );
};
